import { focusWorkaroundService } from './focus-workaround.service';
import { IframeApp } from '../interface/iframe-app';
import { DialogApp } from '../interface/dialog-app';
import { appConfig } from '../../service/config';
import { EventEmitter } from '../../service/event-emitter';

export enum thirdPartyAppEvents {
  UPDATE_APP = 'updateApplication',
  UPDATE_DIALOG = 'updateDialog',
  RELOAD_APP = 'reloadApplication',
  SHOW_BANNER = 'showBanner',
  HIDE_BANNER = 'hideBanner',
}

/*
 * @type class
 * @name @poseidon/third-party-support/ThirdPartySupport
 *
 * @description
 * Stores names of already opened third-party application and determines if
 * one is currently active.
 */
export class ThirdPartySupportService extends EventEmitter {

  /*
   * @type property
   * @name @poseidon/third-party-support/ThirdPartySupport#initializedApps
   *
   * @description
   * Contains a list of initialized apps. Each entry is an object with the keys name, src and active.
   *
   * Important note:
   * When an app is already initialized only change the attributes otherwise
   * the iframe will be destroyed
   */
  initializedApps: IframeApp[] = [];

  /*
   * @type property
   * @name @poseidon/third-party-support/ThirdPartySupport#modalDialogApp
   *
   * @description
   * Contains the current modal dialog application.
   */
  modalDialogApp: DialogApp = null;

  constructor() {
    super();
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartySupport#initApplication
   *
   * @description
   * Adds application name into the list of initialized apps.
   *
   * @param appName {String} Name of a third-party application defined in appConfig
   * @param active {boolean} Bool to set the active state, commonly set as 'false'
   * @param urlKey {String} Key of URL map of the application, commonly set as 'default'
   */
  initApplication(appName: string, active = false, urlKey = 'default'): void {
    const appUrl = this.getUrlFromConfig(this.getApplicationConfig(appName), urlKey);

    const appData: IframeApp = {
      active,
      name: appName,
      src: appUrl,
    };
    this.initializedApps.push(appData);

    this.emit(thirdPartyAppEvents.UPDATE_APP, appData);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartySupport#activate
   *
   * @description
   * Initialize the application and mark it as active.
   *
   * @param appName {String} Name of a third-party application defined in appConfig
   */
  activate(appName: string): void {
    // when the app is already initialized only change the active attribute
    // otherwise Initialize the app and activate it.
    const app = this.initializedApps.find((initApp) => initApp.name === appName);
    if (app) {
      app.active = true;
      this.emit(thirdPartyAppEvents.UPDATE_APP, app);
    } else {
      this.initApplication(appName, true);
    }

    if (appName === 'mail') {
      focusWorkaroundService.apply();
    }

  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartySupport#deactivate
   *
   * @description
   * Marks a third-party application as inactive.
   *
   * @param appName {String} Name of a third-party application defined in appConfig
   */
  deactivate(appName: string): void {
    const app = this.initializedApps.find((initApp) => initApp.name === appName);

    if (app) {
      app.active = false;
      this.emit(thirdPartyAppEvents.UPDATE_APP, app);
    }
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartySupport#reloadApplication
   *
   * @description
   * Reload a third-party application by forcing a iframe reload.
   *
   * @param appName {String} Name of a third-party application defined in appConfig
   * @param urlKey {String} Key of URL map of the application, commonly set as 'default'
   */
  reloadApplication(appName: string, urlKey = 'default'): void {
    const appUrl = this.getUrlFromConfig(this.getApplicationConfig(appName), urlKey);

    if (appUrl) {
      const app = this.initializedApps.find((initApp) => initApp.name === appName);
      app.src = appUrl;
      this.emit(thirdPartyAppEvents.RELOAD_APP, {
        name: appName,
        src: appUrl,
        active: null,
      });
    }
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartySupport#initDialog
   *
   * @description
   * Sets the property this.modalDialogApp to active dialog app {DialogApp}
   *
   * @param appName {String} Name of a third-party application defined in appConfig
   * @param ready {Boolean} Initial ready state of the dialog app
   */
  initDialog(appName: string, ready = true): void {
    const thirdPartyAppConfig = this.getApplicationConfig(appName);
    const appUrl = this.getUrlFromConfig(thirdPartyAppConfig, 'default');

    // Set application as initialized and mark it as active.
    this.modalDialogApp = {
      name: appName,
      spinnerText: thirdPartyAppConfig.loading_text || null,
      src: appUrl,
      dialogProperties: thirdPartyAppConfig.dialog_properties,
      waitForApplicationReady: !!thirdPartyAppConfig.wait_for_application_ready,
      startInBackground: !!thirdPartyAppConfig.start_in_background,
      ready,
    };
    this.emit(thirdPartyAppEvents.UPDATE_DIALOG, this.modalDialogApp);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartySupport#activateDialog
   *
   * @description
   * Sets the current dialog app's ready state to true
   *
   * @param appName {String} Name of a third-party application defined in appConfig
   */
  activateDialog(appName: string): void {
    if (this.modalDialogApp && this.modalDialogApp.name === appName) {
      this.modalDialogApp.ready = true;
      this.emit(thirdPartyAppEvents.UPDATE_DIALOG, this.modalDialogApp);
    } else {
      const activeDialog = this.modalDialogApp ? this.modalDialogApp.name : 'none';
      throw new Error(`No initialized dialog app with name '${appName}'! Active dialog: ${activeDialog}`);
    }
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartySupport#deactivateDialog
   *
   * @description
   * Resets the property this.modalDialogApp to null
   *
   * @param appName {String} Name of an third-party application defined in appConfig
   */
  deactivateDialog(appName: string): void {
    // check if application is available
    if (!this.modalDialogApp || this.modalDialogApp.name !== appName) {
      throw new Error(`Third-party dialog application ${appName} is not active!`);
    }

    // Set application as inactive.
    this.modalDialogApp = null;
    this.emit(thirdPartyAppEvents.UPDATE_DIALOG, this.modalDialogApp);
  }

  showBanner(): void {
    this.emit(thirdPartyAppEvents.SHOW_BANNER);
  }

  hideBanner(): void {
    this.emit(thirdPartyAppEvents.HIDE_BANNER);
  }

  private get thirdPartyApps(): any {
    // Cache the thirdPartyConfig locally. We have cached the thirdPartyConfig
    // above, because the appConfig.get() returns the deeply cloned object,
    // combine that with the huge thirdParty config and you'll have the
    // performance issue.
    const thirdPartyConfig = appConfig.thirdParty;

    return thirdPartyConfig && thirdPartyConfig.navigator && thirdPartyConfig.navigator.apps || {};
  }

  private getApplicationConfig(appName: string) {
    const appConfig = this.thirdPartyApps[appName];

    if (!appConfig) {
      throw new Error(`Third-party application ${appName} not found in the list of available apps!`);
    }

    return appConfig;
  }

  private getUrlFromConfig(thirdPartyAppConfig: any, urlKey: string): any {
    const appUrl = thirdPartyAppConfig.url_map && thirdPartyAppConfig.url_map[urlKey] || '';

    if (!appUrl) {
      throw new Error(`No entry for URL key ${urlKey} in appConfig!`);
    }

    return appUrl;
  }
}

export const thirdPartySupportService = new ThirdPartySupportService();
